import React, { useState, KeyboardEvent, useEffect, Dispatch, SetStateAction, useRef } from 'react';
import Textbox from 'carbon-react/lib/components/textbox';

import { useOnClickOutside } from '../../hooks/hooks';

import './editable-text.scss';
import { useIntl } from 'react-intl';

interface Props {
  text: string;
  onTextChange: (text: string) => void;
  setIsEditing?: Dispatch<SetStateAction<boolean>>;
  initialText: string;
  size?: 'medium' | 'small';
}

export const EditableText = ({ text, initialText, onTextChange, size, setIsEditing }: Props) => {
  const intl = useIntl();
  const [editableState, setEditableState] = useState(false);
  const textInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editableState && !text?.length) {
      onTextChange(initialText);
    }
  }, [editableState, initialText, text, onTextChange]);

  const handleEditText = () => {
    setEditableState(true);
    setIsEditing?.(initialText !== text);
  };

  const handleEnter = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setEditableState(false);
      setIsEditing?.(false);
    }
  };

  const handleBlur = () => {
    setEditableState(false);
    setIsEditing?.(false);
  };

  useOnClickOutside(textInputRef, handleBlur);

  return editableState ? (
    <div ref={textInputRef} style={{ display: 'inline-block', width: '185px' }}>
      <Textbox
        value={text}
        onChange={e => onTextChange(e.target.value)}
        onKeyDown={handleEnter}
        onBlur={handleBlur}
        size={size}
        aria-label={intl.formatMessage({
          id: 'common.editable_text.aria_label',
          defaultMessage: 'Editable text'
        })}
        autoFocus
      />
    </div>
  ) : (
    <span onClick={handleEditText} style={{ cursor: 'pointer' }}>
      {text}
    </span>
  );
};
